import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import {
  ResidentStatus,
  useGetEventsByDateRangeQuery,
  useMarkResidentAsAbsentMutation,
  useUnmarkResidentAsAbsentMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {eventsDateRangeInput} from '@/shared/events/utils/eventsHelpers';

import type {MarkResidentAsAbsentFormValues} from '../utils/residentFormInputs';
import {
  markResidentAsAbsentFormDefaultValues,
  markResidentAsAbsentFormSchema,
} from '../utils/residentFormInputs';
import MarkResidentAsAbsentFormFields from './MarkResidentAsAbsentFormFields';

export type MarkResidentAsAbsentDialogProps = DialogProps;

const MarkResidentAsAbsentDialog = ({
  onOpenChange,
  ...props
}: MarkResidentAsAbsentDialogProps) => {
  const {t} = useTranslation();
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const eventsInput = eventsDateRangeInput();

  const id = searchParams.get('id') || '';
  const isMarkResidentAsAbsent =
    searchParams.get('markResidentAsAbsent') === 'true';

  const revalidateOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: useGetEventsByDateRangeQuery.getKey({input: eventsInput}),
    });
  };

  const markResidentAsAbsent = useMarkResidentAsAbsentMutation(graphqlClient, {
    onSuccess: revalidateOnSuccess,
  });
  const unmarkResidentAsAbsent = useUnmarkResidentAsAbsentMutation(
    graphqlClient,
    {onSuccess: revalidateOnSuccess}
  );

  const isDisabled =
    markResidentAsAbsent.isPending || unmarkResidentAsAbsent.isPending;

  const form = useForm<MarkResidentAsAbsentFormValues>({
    resolver: zodResolver(markResidentAsAbsentFormSchema),
    defaultValues: markResidentAsAbsentFormDefaultValues({
      id: id,
    } as MarkResidentAsAbsentFormValues),
  });

  useEffect(() => {
    if (!id) return;
    form.setValue('id', id, {shouldValidate: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (values: MarkResidentAsAbsentFormValues) => {
    isMarkResidentAsAbsent
      ? await handleMarkResidentAsAbsent(values)
      : await handleUnmarkResidentAsAbsent(values);
  };

  const handleMarkResidentAsAbsent = async (
    values: MarkResidentAsAbsentFormValues
  ) => {
    try {
      await markResidentAsAbsent.mutateAsync({
        input: {
          ...values,
        },
      });
      toast({
        title: t('Resident is marked as absent'),
        variant: 'success',
      });
      handleClose(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUnmarkResidentAsAbsent = async (
    values: MarkResidentAsAbsentFormValues
  ) => {
    try {
      await unmarkResidentAsAbsent.mutateAsync({
        input: {
          ...values,
        },
      });
      toast({
        title: t('Resident is marked as present'),
        variant: 'success',
      });
      handleClose(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = (value: boolean) => {
    form.reset();
    onOpenChange?.(value);
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={handleClose}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              {isMarkResidentAsAbsent && (
                <DialogTitle>{t('Mark resident as absent')}</DialogTitle>
              )}
              {!isMarkResidentAsAbsent && (
                <DialogTitle>{t('Mark resident as present')}</DialogTitle>
              )}
            </DialogHeader>
            <DialogSection>
              <MarkResidentAsAbsentFormFields
                form={form}
                allowedStatuses={
                  isMarkResidentAsAbsent
                    ? [ResidentStatus.Active]
                    : [ResidentStatus.Absent]
                }
              />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  handleClose(false);
                }}
              >
                {t('Close')}
              </Button>
              <Button variant="primary" type="submit" disabled={isDisabled}>
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default MarkResidentAsAbsentDialog;
